import { Dispatch, FC, ReactNode, SetStateAction, createContext, useContext, useState, useEffect } from 'react';

// import { UseTabsState, useTabs } from '../../Tabs';
import { Campaign } from '../../../types/Campaign';
import type { TabSlug } from '../../../pages/campaign/CampaignPage/partials/Tabs/CampaignPageTabs';

interface CampaignPageContextValue {
    campaign: Campaign;
    setCampaign: Dispatch<SetStateAction<Campaign>>;
    // tabs: UseTabsState;
    selectedTab: TabSlug;
    selectTab: (event: any, value: TabSlug) => void;
}

const CampaignPageContext = createContext<CampaignPageContextValue | undefined>(undefined);

const useCampaignPageContext = (): CampaignPageContextValue => {
    const context: CampaignPageContextValue | undefined = useContext(CampaignPageContext);
    if (!context) {
        throw new Error('useCampaignPageContext must be used within a CampaignPageProvider');
    }
    return context;
};

interface UseCampaignPageProps {
    initialCampaign: Campaign;
    selectedTab: TabSlug;
    selectTab: (event: any, value: TabSlug) => void;
}

const useCampaignPage = ({
    initialCampaign,
    selectedTab,
    selectTab,
}: UseCampaignPageProps): CampaignPageContextValue => {
    const [campaign, setCampaign] = useState<Campaign>(initialCampaign);

    // Follow the campaign query: a refetch (e.g. after "sync flights meta" on the
    // overview card) must reach every tab reading this context, not just the first
    // render. react-query keeps the same object while the data is unchanged, so this
    // only fires when the server actually returned something new.
    useEffect(() => {
        setCampaign(initialCampaign);
    }, [initialCampaign]);
    // const tabs: UseTabsState = useTabs();

    return {
        campaign,
        setCampaign,
        // tabs,
        selectedTab,
        selectTab,
    };
};

interface CampaignPageProviderProps {
    children: ReactNode;
    initialCampaign: Campaign;
    selectedTab: TabSlug;
    selectTab: (event: any, value: TabSlug) => void;
}

const CampaignPageProvider: FC<CampaignPageProviderProps> = ({ children, initialCampaign, selectedTab, selectTab }) => {
    const value = useCampaignPage({ initialCampaign, selectedTab, selectTab });

    return <CampaignPageContext.Provider value={value}>{children}</CampaignPageContext.Provider>;
};

export type { CampaignPageContextValue };
export { CampaignPageContext };
export { useCampaignPageContext };

export type { CampaignPageProviderProps };
export { CampaignPageProvider };
